import type { NavigationGuard } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { useTenantStore } from '@/stores/tenant'
import { authEnabled } from '@/auth/oidc'

/* Redirect-based auth: an unauthenticated hit on a protected route starts the
   OIDC login (redirect to the IdP), so there is no in-app password screen.
   The guard returns false to cancel the local navigation while the browser
   leaves for the IdP. */
export const authGuard: NavigationGuard = async (to) => {
  if (to.meta.public) return true

  const session = useSessionStore()
  if (to.meta.requiresAuth && !session.isAuthenticated) {
    if (!authEnabled()) {
      // Tenant has no IdP configured yet (e.g. PLCC pre-launch).
      return { name: 'not-found', params: { pathMatch: to.path.slice(1).split('/') } }
    }
    await session.login(to.fullPath)
    return false
  }
  return true
}

export const featureGuard: NavigationGuard = (to) => {
  const feature = to.meta.requiresFeature
  if (!feature) return true
  /* Dev-only routes (e.g. the styleguide docs) stay reachable locally even when
     the tenant feature flag is off, so developers can view them; end users on a
     production build never can. */
  if (import.meta.env.DEV && to.meta.devOnly) return true
  const tenant = useTenantStore()
  if (tenant.current.features[feature]) return true
  return { name: 'not-found', params: { pathMatch: to.path.slice(1).split('/') } }
}
